import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Mail, Trash2, Eye, ArrowLeft, Inbox, Building2, Hammer, LogOut, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/jsnv4v6a_1000368650.jpg";

function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO_URL} alt="KMSN" className="h-16 w-16 object-contain rounded mb-4" />
          <h1 className="font-heading text-2xl text-white">Connexion Admin</h1>
          <div className="h-[2px] bg-[#C8A951] w-8 mt-3" />
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">Email</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="admin@email.com" className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] h-12" data-testid="admin-email-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">Mot de passe</label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="********" className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] h-12" data-testid="admin-password-input" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-[#C8A951] text-black font-medium tracking-wide py-3 hover:bg-white transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 font-body uppercase text-sm" data-testid="admin-login-btn">
            <Lock size={16} />
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
        <Link to="/" className="flex items-center justify-center gap-2 mt-8 text-[#A1A1AA] hover:text-white text-sm font-body transition-colors" data-testid="admin-back-to-site">
          <ArrowLeft size={14} /> Retour au site
        </Link>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, construction: 0, travaux: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const fetchData = async () => {
    try {
      const [msgRes, statsRes] = await Promise.all([
        axios.get(`${API}/messages`, { withCredentials: true }),
        axios.get(`${API}/messages/stats`, { withCredentials: true }),
      ]);
      setMessages(msgRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { if (user) fetchData(); }, [user]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const markRead = async (id) => {
    try { await axios.patch(`${API}/messages/${id}/read`, {}, { withCredentials: true }); fetchData(); } catch (err) { console.error(err); }
  };

  const deleteMsg = async (id) => {
    try { await axios.delete(`${API}/messages/${id}`, { withCredentials: true }); if (selected?.id === id) setSelected(null); fetchData(); } catch (err) { console.error(err); }
  };

  const viewMessage = (msg) => { setSelected(msg); if (!msg.is_read) markRead(msg.id); };

  if (checking) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><p className="text-[#A1A1AA] font-body">Chargement...</p></div>;
  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-page">
      <div className="border-b border-white/5 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="KMSN" className="h-8 w-8 object-contain rounded" />
            <span className="font-heading text-lg text-white">KMSN Admin</span>
            <span className="text-[#A1A1AA] text-xs font-body ml-2 hidden sm:inline">{user.email}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white text-sm font-body transition-colors" data-testid="admin-back-link"><ArrowLeft size={16} />Retour</Link>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[#A1A1AA] hover:text-red-400 text-sm font-body transition-colors" data-testid="admin-logout-btn"><LogOut size={16} />D&eacute;connexion</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Inbox, label: "Total messages", value: stats.total, color: "text-white" },
            { icon: Mail, label: "Non lus", value: stats.unread, color: "text-[#C8A951]" },
            { icon: Building2, label: "Construction", value: stats.construction, color: "text-blue-400" },
            { icon: Hammer, label: "Travaux", value: stats.travaux, color: "text-green-400" },
          ].map((s, i) => (
            <div key={i} className="bg-[#1A1A1A] border border-white/5 p-4 sm:p-6" data-testid={`admin-stat-${i}`}>
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} strokeWidth={1.5} />
              <p className="font-heading text-2xl sm:text-3xl text-white">{s.value}</p>
              <p className="text-[#A1A1AA] text-xs font-body mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5"><h2 className="font-heading text-lg text-white">Messages re&ccedil;us</h2></div>
            {loading ? (
              <div className="p-8 text-center text-[#A1A1AA] font-body">Chargement...</div>
            ) : messages.length === 0 ? (
              <div className="p-12 text-center"><Inbox className="w-12 h-12 text-[#A1A1AA]/30 mx-auto mb-4" /><p className="text-[#A1A1AA] font-body">Aucun message</p></div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body">Nom</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body">Sujet</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body">Source</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body">Date</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-body text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messages.map((msg) => (
                      <TableRow key={msg.id} className={`border-white/5 cursor-pointer transition-colors ${!msg.is_read ? "bg-[#C8A951]/5" : ""} ${selected?.id === msg.id ? "bg-white/5" : "hover:bg-white/5"}`} onClick={() => viewMessage(msg)} data-testid={`admin-message-row-${msg.id}`}>
                        <TableCell className="font-body text-sm text-white"><div className="flex items-center gap-2">{!msg.is_read && <div className="w-2 h-2 rounded-full bg-[#C8A951]" />}{msg.name}</div></TableCell>
                        <TableCell className="font-body text-sm text-[#A1A1AA]">{msg.subject}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-xs border-white/10 font-body ${msg.source === "construction" ? "text-blue-400" : msg.source === "travaux" ? "text-green-400" : "text-[#A1A1AA]"}`}>{msg.source}</Badge></TableCell>
                        <TableCell className="font-body text-xs text-[#A1A1AA]">{new Date(msg.created_at).toLocaleDateString("fr-FR")}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={(e) => { e.stopPropagation(); viewMessage(msg); }} className="text-[#A1A1AA] hover:text-white p-1"><Eye size={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); deleteMsg(msg.id); }} className="text-[#A1A1AA] hover:text-red-400 p-1"><Trash2 size={14} /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <div className="bg-[#1A1A1A] border border-white/5">
            <div className="p-4 border-b border-white/5"><h2 className="font-heading text-lg text-white">D&eacute;tails</h2></div>
            {selected ? (
              <div className="p-6 space-y-4" data-testid="admin-message-detail">
                {[
                  { label: "Nom", value: selected.name },
                  { label: "Email", value: selected.email },
                  ...(selected.phone ? [{ label: "T\u00e9l\u00e9phone", value: selected.phone }] : []),
                  { label: "Sujet", value: selected.subject },
                ].map((item, i) => (
                  <div key={i}><p className="text-xs text-[#A1A1AA] font-body uppercase tracking-wider mb-1">{item.label}</p><p className="text-white font-body">{item.value}</p></div>
                ))}
                <div><p className="text-xs text-[#A1A1AA] font-body uppercase tracking-wider mb-1">Message</p><p className="text-[#A1A1AA] font-body text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p></div>
                <div><p className="text-xs text-[#A1A1AA] font-body uppercase tracking-wider mb-1">Source</p><Badge variant="outline" className="text-xs border-white/10 font-body text-[#C8A951]">{selected.source}</Badge></div>
                <div><p className="text-xs text-[#A1A1AA] font-body uppercase tracking-wider mb-1">Date</p><p className="text-white font-body text-sm">{new Date(selected.created_at).toLocaleString("fr-FR")}</p></div>
                <button onClick={() => deleteMsg(selected.id)} className="w-full mt-4 border border-red-400/30 text-red-400 py-2 text-sm font-body hover:bg-red-400/10 transition-colors" data-testid="admin-detail-delete-btn">Supprimer ce message</button>
              </div>
            ) : (
              <div className="p-12 text-center"><Mail className="w-10 h-10 text-[#A1A1AA]/20 mx-auto mb-3" /><p className="text-[#A1A1AA] text-sm font-body">S&eacute;lectionnez un message</p></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
